/* eslint brace-style: ["error", "allman"] */
/**
 * Pure rules for approval workflow configuration. See README.md.
 */

// These lists and readActorSpec must match api/approvals/source/Workflow.js and WorkflowProvider.js.
const ROLE_ACTOR_TYPES = [ 'ProjectRole', 'UserRole', 'UserRoleProjectMembership', 'FormLabRole', 'FormMaterialLabRole', 'ProjectEntitlement' ];
const KEYWORD_ACTOR_TYPES = [ 'Admin', 'CreatingUser', 'HigherUserLevelInProject', 'MinimumUserLevelInProject', 'FormLabUser', 'FormMaterialLabInitialUser', 'FormMaterialLabEscalationUser' ];
const ACTOR_TYPES = ROLE_ACTOR_TYPES.concat(KEYWORD_ACTOR_TYPES, [ 'Users' ]);
const CRITERION_TYPES = [ 'True', 'False', 'EntityOwner', 'HasProjectSetting', 'FormDataMetadata', 'ChangeOrderThreshold', 'DocumentHasObsWithLabel', 'RecordNotFound' ];
const TRIGGER_EVENTS = [ 'ProjectFilePromote', 'LockDocumentSample', 'UnlockDocumentSample', 'SetDocumentSampleStatus', 'LIMSClaim' ];
const STATE_KEYS = [ 'Status', 'StateName', 'Transitions' ];
const TRANSITION_KEYS = [ 'Action', 'ToStatus', 'Actors', 'LockEntity', 'UnlockEntity', 'AllowOnLocked', 'IsApproval', 'InvalidatesApproval', 'CreateLineItem', 'RequireProjectSetting', 'AdditionalRequiredFields', 'Criteria', 'Branches', 'BackPropagateTo', 'BackPropagateCriteria', 'InferBackPropagateCriteria', 'Triggers' ];
// The approvals service and document code key on these by name.
const CONVENTIONAL_STATES = [ 'Draft', 'Approved', 'Rejected' ];
const SPECIAL_STATES = [ 'Draft', 'Approved' ];

/**
 * @typedef {object} ActorSpec
 * @property {string} Type - The actor type.
 * @property {Array<any>} Settings - Its roles, users or values.
 * @property {Array<string>} Tags - Its tags.
 * @property {string|null} Field - The object field holding the settings; null for a string actor or an actor with none.
 */

/**
 * @typedef {object} Finding
 * @property {string} Code - Stable identifier of the rule.
 * @property {'error'|'warning'|'info'} Severity - How serious it is.
 * @property {string} Message - What's wrong, for the person editing.
 * @property {string} [Status] - The state it concerns.
 * @property {number} [TransitionIndex] - The transition it concerns, within that state.
 */

/**
 * @typedef {object} CheckContext
 * @property {Set<string>} [KnownRoleCodes] - Role.PermissionLevel codes in the customer.
 * @property {Record<string, { Disabled?: any, IDCustomer?: number }>} [Users] - The users named by actors, by IDUser.
 * @property {number} [IDCustomer] - The customer the workflow belongs to.
 * @property {any} [Baseline] - The workflow as last saved, to notice risky renames.
 */

/**
 * Read an actor in any form the approvals service accepts, in the same order it reads them.
 *
 * @param {any} pActor - A string or object actor.
 *
 * @return {ActorSpec} The normalized actor.
 */
function readActorSpec(pActor)
{
	if (pActor && (typeof pActor === 'object'))
	{
		const tmpTags = Array.isArray(pActor.Tags) ? pActor.Tags : [];
		for (const tmpField of [ 'IDs', 'Values', 'Hashes' ])
		{
			if (Array.isArray(pActor[tmpField]))
			{
				return { Type: pActor.Type, Settings: pActor[tmpField], Tags: tmpTags, Field: tmpField };
			}
		}
		for (const tmpField of [ 'Value', 'Hash', 'ID' ])
		{
			if (pActor[tmpField] !== undefined)
			{
				return { Type: pActor.Type, Settings: [ pActor[tmpField] ], Tags: tmpTags, Field: tmpField };
			}
		}
		if (typeof pActor.Settings === 'string')
		{
			return { Type: pActor.Type, Settings: pActor.Settings.split(',').filter(Boolean), Tags: tmpTags, Field: 'Settings' };
		}
		return { Type: pActor.Type, Settings: [], Tags: tmpTags, Field: null };
	}
	const tmpString = String(pActor || '');
	const tmpSeparator = tmpString.indexOf(':');
	if (tmpSeparator > -1)
	{
		return { Type: tmpString.substring(0, tmpSeparator), Settings: tmpString.substring(tmpSeparator + 1).split(',').filter(Boolean), Tags: [], Field: null };
	}
	return { Type: tmpString, Settings: [], Tags: [], Field: null };
}

/**
 * Visit every transition of a workflow, including nested branches.
 *
 * @param {any} pWorkflow - A workflow.
 * @param {(pEdge: any, pState: any, pPath: string, pTransition: any, pTransitionIndex: number) => void} fVisit - Called per transition or branch.
 */
function forEachEdge(pWorkflow, fVisit)
{
	const fWalk = (pEdge, pState, pPath, pTransition, pTransitionIndex) =>
	{
		fVisit(pEdge, pState, pPath, pTransition, pTransitionIndex);
		(Array.isArray(pEdge.Branches) ? pEdge.Branches : []).forEach((pBranch, pIndex) => fWalk(pBranch, pState, `${pPath} branch ${pIndex + 1}`, pTransition, pTransitionIndex));
	};
	((pWorkflow && pWorkflow.States) || []).forEach((pState) =>
	{
		(Array.isArray(pState && pState.Transitions) ? pState.Transitions : []).forEach((pTransition, pIndex) =>
		{
			fWalk(pTransition, pState, `[${pState.Status}] ${pTransition.Action || `transition ${pIndex + 1}`}`, pTransition, pIndex);
		});
	});
}

/**
 * Literal deep equality as the config service stores values: object key order doesn't matter, array order does.
 *
 * @param {any} pA - First value.
 * @param {any} pB - Second value.
 *
 * @return {boolean} True when equal.
 */
function deepEqual(pA, pB)
{
	if (pA === pB)
	{
		return true;
	}
	if ((typeof pA !== 'object') || (typeof pB !== 'object') || (pA === null) || (pB === null))
	{
		return false;
	}
	if (Array.isArray(pA) !== Array.isArray(pB))
	{
		return false;
	}
	if (Array.isArray(pA))
	{
		return (pA.length === pB.length) && pA.every((pValue, pIndex) => deepEqual(pValue, pB[pIndex]));
	}
	const tmpKeysA = Object.keys(pA);
	const tmpKeysB = Object.keys(pB);
	if (tmpKeysA.length !== tmpKeysB.length)
	{
		return false;
	}
	return tmpKeysA.every((pKey) => Object.prototype.hasOwnProperty.call(pB, pKey) && deepEqual(pA[pKey], pB[pKey]));
}

/**
 * A workflow name is one URL segment.
 *
 * @param {string} pName - A proposed workflow name.
 *
 * @return {string} Why it can't be used; empty when it can.
 */
function workflowNameProblem(pName)
{
	const tmpName = String(pName == null ? '' : pName);
	if (!tmpName.trim())
	{
		return 'A workflow needs a name.';
	}
	if (/[/\\?#%'"]/.test(tmpName))
	{
		return 'A workflow name can\'t contain / \\ ? # % or quotes.';
	}
	return '';
}

/**
 * States other than the first that nothing transitions into, so only documents already there can be in them.
 *
 * @param {any} pWorkflow - A workflow.
 *
 * @return {Array<any>} The states.
 */
function unreachableStateRecords(pWorkflow)
{
	const tmpReachable = new Set();
	forEachEdge(pWorkflow, (pEdge) =>
	{
		tmpReachable.add(pEdge.ToStatus);
	});
	return ((pWorkflow && pWorkflow.States) || []).slice(1).filter((pState) => !tmpReachable.has(pState.Status));
}

/**
 * @param {any} pWorkflow - A workflow.
 *
 * @return {Array<string>} The names of states nothing transitions into.
 */
function findUnreachableStates(pWorkflow)
{
	return unreachableStateRecords(pWorkflow).map((pState) => pState.StateName || pState.Status);
}

/**
 * @param {any} pWorkflow - A workflow.
 *
 * @return {Array<string>} One line per ToStatus or BackPropagateTo that names no state.
 */
function findDanglingTargets(pWorkflow)
{
	const tmpStatuses = new Set(((pWorkflow && pWorkflow.States) || []).map((pState) => pState.Status));
	const tmpDangling = [];
	forEachEdge(pWorkflow, (pEdge, pState, pPath, pTransition) =>
	{
		if ((pEdge.ToStatus !== undefined) && !tmpStatuses.has(pEdge.ToStatus))
		{
			tmpDangling.push(`${pState.Status} '${pTransition.Action}' → ${pEdge.ToStatus}`);
		}
		(Array.isArray(pEdge.BackPropagateTo) ? pEdge.BackPropagateTo : []).filter((pTarget) => !tmpStatuses.has(pTarget)).forEach((pTarget) =>
		{
			tmpDangling.push(`${pState.Status} '${pTransition.Action}' back-propagates to ${pTarget}`);
		});
	});
	return tmpDangling;
}

/**
 * Where a transition can end, after branch fields merge over their parent's the way the service merges them.
 *
 * @param {any} pTransition - A transition.
 *
 * @return {Array<any>} One merged edge for the transition and for each branch path.
 */
function transitionOutcomes(pTransition)
{
	const tmpOutcomes = [];
	const fWalk = (pEdge, pMerged) =>
	{
		const tmpMerged = Object.assign({}, pMerged, pEdge);
		tmpOutcomes.push(tmpMerged);
		(Array.isArray(pEdge.Branches) ? pEdge.Branches : []).forEach((pBranch) => fWalk(pBranch, tmpMerged));
	};
	fWalk(pTransition, {});
	return tmpOutcomes;
}

/**
 * Work out which lock conditions a document can be in at each state, starting unlocked at the first state and at any
 * state nothing leads to. A locked document can only leave by an action with AllowOnLocked.
 *
 * @param {any} pWorkflow - A workflow.
 *
 * @return {Map<string, Set<boolean>>} Status to the lock conditions it can be reached in.
 */
function reachableLockStates(pWorkflow)
{
	const tmpStates = Array.isArray(pWorkflow && pWorkflow.States) ? pWorkflow.States : [];
	const tmpByStatus = new Map(tmpStates.map((pState) => [ pState.Status, pState ]));
	const tmpLockStates = new Map(tmpStates.map((pState) => [ pState.Status, new Set() ]));
	const tmpQueue = [];
	const fReach = (pStatus, pLocked) =>
	{
		const tmpSeen = tmpLockStates.get(pStatus);
		if (tmpSeen && !tmpSeen.has(pLocked))
		{
			tmpSeen.add(pLocked);
			tmpQueue.push([ pStatus, pLocked ]);
		}
	};
	if (tmpStates.length)
	{
		fReach(tmpStates[0].Status, false);
	}
	unreachableStateRecords(pWorkflow).forEach((pState) => fReach(pState.Status, false));
	while (tmpQueue.length)
	{
		const [ tmpStatus, tmpLocked ] = tmpQueue.shift();
		const tmpState = tmpByStatus.get(tmpStatus);
		(Array.isArray(tmpState.Transitions) ? tmpState.Transitions : []).forEach((pTransition) =>
		{
			if (tmpLocked && !pTransition.AllowOnLocked)
			{
				return;
			}
			transitionOutcomes(pTransition).forEach((pOutcome) =>
			{
				fReach(pOutcome.ToStatus, !!(pOutcome.LockEntity || (tmpLocked && !pOutcome.UnlockEntity)));
			});
		});
	}
	return tmpLockStates;
}

/**
 * @param {string} pKey - A key the service doesn't read.
 *
 * @return {string} A hint when it looks like a misspelling of one it does.
 */
function misspellingHint(pKey)
{
	const tmpMatch = TRANSITION_KEYS.find((pKnown) => pKnown.toLowerCase() === String(pKey).toLowerCase());
	return tmpMatch ? ` Did you mean ${tmpMatch}?` : '';
}

/**
 * Check a workflow against what the approvals service does with it. Rules whose context is missing are skipped.
 *
 * @param {any} pWorkflow - A workflow.
 * @param {CheckContext} [pContext] - What's known beyond the workflow.
 *
 * @return {Array<Finding>} What's wrong; errors first.
 */
function checkWorkflow(pWorkflow, pContext)
{
	const tmpContext = pContext || {};
	const tmpFindings = [];
	/**
	 * @param {string} pCode
	 * @param {'error'|'warning'|'info'} pSeverity
	 * @param {string} pMessage
	 * @param {string} [pStatus]
	 * @param {number} [pTransitionIndex]
	 */
	const fAdd = (pCode, pSeverity, pMessage, pStatus, pTransitionIndex) =>
	{
		const tmpFinding = { Code: pCode, Severity: pSeverity, Message: pMessage };
		if (pStatus !== undefined)
		{
			tmpFinding.Status = pStatus;
		}
		if (pTransitionIndex !== undefined)
		{
			tmpFinding.TransitionIndex = pTransitionIndex;
		}
		tmpFindings.push(tmpFinding);
	};
	const tmpStates = (pWorkflow && Array.isArray(pWorkflow.States)) ? pWorkflow.States : [];
	if (!tmpStates.length)
	{
		fAdd('no-states', 'error', 'A workflow needs at least one state.');
		return tmpFindings;
	}

	const tmpStatuses = new Set();
	tmpStates.forEach((pState, pIndex) =>
	{
		if (!pState || (typeof pState.Status !== 'string') || !pState.Status)
		{
			fAdd('missing-status', 'error', `State ${pIndex + 1} (${(pState && pState.StateName) || 'unnamed'}) has no state hash.`);
			return;
		}
		if (tmpStatuses.has(pState.Status))
		{
			fAdd('duplicate-status', 'error', `More than one state uses the hash "${pState.Status}".`, pState.Status);
		}
		tmpStatuses.add(pState.Status);
		if (!Array.isArray(pState.Transitions))
		{
			fAdd('transitions-not-list', 'error', `"${pState.Status}" has no list of transitions. Use an empty list for a final state.`, pState.Status);
		}
		if (typeof pState.StateName !== 'string')
		{
			fAdd('missing-state-name', 'info', `"${pState.Status}" has no display name, so people will see its hash.`, pState.Status);
		}
		Object.keys(pState).filter((pKey) => !STATE_KEYS.includes(pKey) && (pKey !== 'StateIndex')).forEach((pKey) =>
		{
			fAdd('unknown-key', 'info', `"${pState.Status}" has a setting the approvals service doesn't read: ${pKey}.`, pState.Status);
		});
	});

	const tmpLockStates = reachableLockStates(pWorkflow);
	tmpStates.filter((pState) => pState && Array.isArray(pState.Transitions)).forEach((pState) =>
	{
		const tmpSeenActions = new Set();
		const tmpLocks = tmpLockStates.get(pState.Status);
		const tmpAlwaysLocked = !!tmpLocks && (tmpLocks.size === 1) && tmpLocks.has(true);
		pState.Transitions.forEach((pTransition, pIndex) =>
		{
			const tmpAction = String(pTransition.Action || '');
			if (!tmpAction.trim())
			{
				fAdd('missing-action', 'error', `A transition from "${pState.Status}" has no action name.`, pState.Status, pIndex);
			}
			else if (tmpSeenActions.has(tmpAction))
			{
				fAdd('duplicate-action', 'warning', `"${pState.Status}" has two "${tmpAction}" transitions. Everyone on both is offered the action, but only the first is ever used, so the second group is refused.`, pState.Status, pIndex);
			}
			tmpSeenActions.add(tmpAction);
			if (!Array.isArray(pTransition.Actors) || !pTransition.Actors.length)
			{
				fAdd('no-actors', 'warning', `Nobody can take "${tmpAction}" from "${pState.Status}". Documents that depend on it get stuck.`, pState.Status, pIndex);
			}
			if (tmpAlwaysLocked && !pTransition.AllowOnLocked)
			{
				fAdd('locked-action', 'warning', `"${tmpAction}" is refused: documents are always locked in "${pState.Status}" and this action isn't allowed on locked documents.`, pState.Status, pIndex);
			}
			if (/^approve/i.test(tmpAction) && !pTransition.IsApproval)
			{
				fAdd('approve-without-flag', 'info', `"${tmpAction}" from "${pState.Status}" isn't marked as an approval, so it won't count as one.`, pState.Status, pIndex);
			}
			if (/reject|void|return|deny/i.test(tmpAction) && pTransition.IsApproval)
			{
				fAdd('reject-with-flag', 'info', `"${tmpAction}" from "${pState.Status}" is marked as an approval, so it will count as one.`, pState.Status, pIndex);
			}
			(Array.isArray(pTransition.Actors) ? pTransition.Actors : []).forEach((pActor) =>
			{
				if (!pActor || !((typeof pActor === 'string') || (typeof pActor.Type === 'string')))
				{
					fAdd('bad-actor', 'warning', `"${tmpAction}" from "${pState.Status}" has an actor the approvals service can't read: ${JSON.stringify(pActor)}.`, pState.Status, pIndex);
					return;
				}
				const tmpSpec = readActorSpec(pActor);
				if (!ACTOR_TYPES.includes(tmpSpec.Type))
				{
					fAdd('unknown-actor-type', 'warning', `"${tmpAction}" from "${pState.Status}" names an actor type the approvals service doesn't know, so it matches nobody: ${tmpSpec.Type}.`, pState.Status, pIndex);
				}
				if (tmpContext.KnownRoleCodes && ROLE_ACTOR_TYPES.includes(tmpSpec.Type))
				{
					tmpSpec.Settings.filter((pCode) => !tmpContext.KnownRoleCodes.has(String(pCode))).forEach((pCode) =>
					{
						fAdd('unknown-role', 'warning', `"${tmpAction}" from "${pState.Status}" names a role this customer doesn't have, so it matches nobody: ${pCode}.`, pState.Status, pIndex);
					});
				}
				if (tmpContext.Users && (tmpSpec.Type === 'Users'))
				{
					tmpSpec.Settings.forEach((pID) =>
					{
						const tmpUser = tmpContext.Users[String(pID)];
						if (!tmpUser)
						{
							fAdd('unknown-user', 'warning', `"${tmpAction}" from "${pState.Status}" names user ${pID}, who couldn't be found.`, pState.Status, pIndex);
						}
						else if (tmpUser.Disabled)
						{
							fAdd('disabled-user', 'warning', `"${tmpAction}" from "${pState.Status}" names user ${pID}, who is disabled.`, pState.Status, pIndex);
						}
						else if (tmpContext.IDCustomer && tmpUser.IDCustomer && (Number(tmpUser.IDCustomer) !== Number(tmpContext.IDCustomer)))
						{
							fAdd('other-customer-user', 'warning', `"${tmpAction}" from "${pState.Status}" names user ${pID}, who belongs to another customer.`, pState.Status, pIndex);
						}
					});
				}
			});
		});
	});

	forEachEdge(pWorkflow, (pEdge, pState, pPath, pTransition, pTransitionIndex) =>
	{
		const tmpIsBranch = pEdge !== pTransition;
		if ((pEdge.ToStatus === undefined) ? !tmpIsBranch : !tmpStatuses.has(pEdge.ToStatus))
		{
			fAdd('dangling-target', 'warning', `${pPath} goes to "${pEdge.ToStatus}", which isn't a state in this workflow.`, pState.Status, pTransitionIndex);
		}
		(Array.isArray(pEdge.BackPropagateTo) ? pEdge.BackPropagateTo : []).filter((pTarget) => !tmpStatuses.has(pTarget)).forEach((pTarget) =>
		{
			fAdd('dangling-target', 'warning', `${pPath} back-propagates to "${pTarget}", which isn't a state in this workflow.`, pState.Status, pTransitionIndex);
		});
		if (tmpIsBranch && !(Array.isArray(pEdge.Criteria) && pEdge.Criteria.length))
		{
			fAdd('branch-no-criteria', 'warning', `${pPath} has no conditions, so it's never used.`, pState.Status, pTransitionIndex);
		}
		[].concat(Array.isArray(pEdge.Criteria) ? pEdge.Criteria : [], Array.isArray(pEdge.BackPropagateCriteria) ? pEdge.BackPropagateCriteria : []).forEach((pCriterion) =>
		{
			if (!pCriterion || !CRITERION_TYPES.includes(pCriterion.Type))
			{
				fAdd('unknown-criterion', 'warning', `${pPath} has a condition the approvals service doesn't know, so it never matches: ${(pCriterion && pCriterion.Type) || JSON.stringify(pCriterion)}.`, pState.Status, pTransitionIndex);
			}
		});
		(Array.isArray(pEdge.Triggers) ? pEdge.Triggers : []).filter((pTrigger) => !pTrigger || !TRIGGER_EVENTS.includes(pTrigger.Event)).forEach((pTrigger) =>
		{
			fAdd('unknown-trigger', 'warning', `${pPath} has a trigger the approvals service doesn't know: ${(pTrigger && pTrigger.Event) || JSON.stringify(pTrigger)}.`, pState.Status, pTransitionIndex);
		});
		Object.keys(pEdge).filter((pKey) => !TRANSITION_KEYS.includes(pKey)).forEach((pKey) =>
		{
			fAdd('unknown-key', 'info', `${pPath} has a setting the approvals service doesn't read: ${pKey}.${misspellingHint(pKey)}`, pState.Status, pTransitionIndex);
		});
	});

	unreachableStateRecords(pWorkflow).forEach((pState) =>
	{
		fAdd('unreachable-state', 'info', `Nothing leads to "${pState.Status}". Only documents already there can be in it.`, pState.Status);
	});
	CONVENTIONAL_STATES.filter((pStatus) => !tmpStatuses.has(pStatus)).forEach((pStatus) =>
	{
		fAdd('missing-conventional-state', 'info', `There's no "${pStatus}" state. Some document and finance behavior depends on Draft, Approved and Rejected by name.`);
	});

	const tmpBaseline = tmpContext.Baseline;
	if (tmpBaseline && Array.isArray(tmpBaseline.States))
	{
		const tmpHasPreApprovals = tmpBaseline.States.some((pState) => (pState.Transitions || []).some((pTransition) => Array.isArray(pTransition.BackPropagateTo) && pTransition.BackPropagateTo.length));
		tmpBaseline.States.forEach((pOldState) =>
		{
			const tmpCurrent = tmpStates.find((pState) => pState && (pState.Status === pOldState.Status));
			if (!tmpCurrent)
			{
				if (SPECIAL_STATES.includes(pOldState.Status))
				{
					fAdd('renamed-special-state', 'warning', `"${pOldState.Status}" is gone. Some document and finance behavior depends on that name.`);
				}
				return;
			}
			if (tmpHasPreApprovals)
			{
				(pOldState.Transitions || []).filter((pOld) => !(tmpCurrent.Transitions || []).some((pNew) => pNew.Action === pOld.Action)).forEach((pOld) =>
				{
					fAdd('renamed-action', 'warning', `"${pOld.Action}" from "${pOldState.Status}" is gone. Pending pre-approvals store the action name, so any waiting on it will fail.`, pOldState.Status);
				});
			}
		});
	}

	const tmpOrder = { error: 0, warning: 1, info: 2 };
	return tmpFindings.sort((pA, pB) => tmpOrder[pA.Severity] - tmpOrder[pB.Severity]);
}

/**
 * Re-key a workflow by Status and transitions by Action, so a comparison names states instead of array positions.
 *
 * @param {any} pWorkflow - A workflow.
 *
 * @return {any} The keyed form (transitions with duplicate actions get a #n suffix).
 */
function keyedWorkflow(pWorkflow)
{
	/** @type {any} */
	const tmpKeyed = {};
	Object.keys(pWorkflow || {}).filter((pKey) => pKey !== 'States').forEach((pKey) =>
	{
		tmpKeyed[pKey] = pWorkflow[pKey];
	});
	tmpKeyed.States = {};
	((pWorkflow && pWorkflow.States) || []).forEach((pState, pIndex) =>
	{
		const tmpState = Object.assign({}, pState, { Position: pIndex + 1, Transitions: {} });
		((pState && pState.Transitions) || []).forEach((pEdge, pEdgeIndex) =>
		{
			let tmpKey = pEdge.Action || `#${pEdgeIndex + 1}`;
			while (Object.prototype.hasOwnProperty.call(tmpState.Transitions, tmpKey))
			{
				tmpKey = `${tmpKey}#${pEdgeIndex + 1}`;
			}
			tmpState.Transitions[tmpKey] = Object.assign({ Position: pEdgeIndex + 1 }, pEdge);
		});
		tmpKeyed.States[(pState && pState.Status) || `#${pIndex + 1}`] = tmpState;
	});
	return tmpKeyed;
}

/**
 * @param {any} pValue - A value to show in a change line.
 *
 * @return {string} Compact JSON, cut at 160 characters.
 */
function brief(pValue)
{
	const tmpText = (pValue === undefined) ? '(none)' : JSON.stringify(pValue);
	return (tmpText.length > 160) ? `${tmpText.slice(0, 157)}...` : tmpText;
}

/**
 * List the differences between two values as "path: from → to" lines. Objects recurse; arrays and scalars compare whole.
 *
 * @param {any} pFrom - The earlier value.
 * @param {any} pTo - The later value.
 * @param {string} [pPath] - The path so far.
 * @param {Array<string>} [pLines] - Accumulator.
 *
 * @return {Array<string>} The change lines.
 */
function diffLines(pFrom, pTo, pPath, pLines)
{
	const tmpLines = pLines || [];
	const tmpPath = pPath || '';
	const fIsObject = (pValue) => pValue && (typeof pValue === 'object') && !Array.isArray(pValue);
	if (deepEqual(pFrom, pTo))
	{
		return tmpLines;
	}
	if (fIsObject(pFrom) && fIsObject(pTo))
	{
		Array.from(new Set(Object.keys(pFrom).concat(Object.keys(pTo)))).forEach((pKey) =>
		{
			const tmpChildPath = tmpPath ? `${tmpPath} › ${pKey}` : pKey;
			if (!Object.prototype.hasOwnProperty.call(pFrom, pKey))
			{
				tmpLines.push(`+ ${tmpChildPath}: ${brief(pTo[pKey])}`);
			}
			else if (!Object.prototype.hasOwnProperty.call(pTo, pKey))
			{
				tmpLines.push(`- ${tmpChildPath}: ${brief(pFrom[pKey])}`);
			}
			else
			{
				diffLines(pFrom[pKey], pTo[pKey], tmpChildPath, tmpLines);
			}
		});
		return tmpLines;
	}
	tmpLines.push(`~ ${tmpPath || '(value)'}: ${brief(pFrom)} → ${brief(pTo)}`);
	return tmpLines;
}

/**
 * What changed between two versions of a workflow, by state and action name rather than position.
 *
 * @param {any} pFrom - The earlier workflow.
 * @param {any} pTo - The later workflow.
 *
 * @return {Array<string>} The change lines.
 */
function workflowChanges(pFrom, pTo)
{
	return diffLines(keyedWorkflow(pFrom), keyedWorkflow(pTo));
}

module.exports =
{
	ROLE_ACTOR_TYPES,
	KEYWORD_ACTOR_TYPES,
	ACTOR_TYPES,
	CRITERION_TYPES,
	TRIGGER_EVENTS,
	TRANSITION_KEYS,
	readActorSpec,
	forEachEdge,
	deepEqual,
	workflowNameProblem,
	findUnreachableStates,
	findDanglingTargets,
	transitionOutcomes,
	checkWorkflow,
	keyedWorkflow,
	diffLines,
	workflowChanges,
};
